import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {DeviceMeasurement} from "../../../../model/device-measurement";
import {Tenant} from "../../../../model/tenant";
import {AbstractControl, FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {MeasurementType} from "../../../../model/measurement-type";
import {ActionButton} from "../../../../model/ui/action-button";
import {AssetMeasurement} from "../../../../model/asset-measurement";
import {TenantsService} from "../../../../services/tenants.service";
import {PreviousRouteService} from "../../../../services/previous-route.service";
import {TemplatesService} from "../../../../services/templates.service";
import {noSpecialCharacters} from "../../../../shared/custom-validators";
import {Template} from "../../../../model/template";

@Component({
  selector: 'app-templates-form-measurements',
  templateUrl: './templates-form-measurements.component.html',
  styleUrls: ['./templates-form-measurements.component.scss']
})
export class TemplatesFormMeasurementsComponent implements OnInit {

  @Input() mode: 'create' | 'edit';

  @Input() type: string;
  @Input() deviceMeasurements: DeviceMeasurement[];
  @Input() deviceExpressions: DeviceMeasurement[];
  @Input() assetMeasurements: AssetMeasurement[];
  @Output() deviceMeasurementsChange = new EventEmitter<DeviceMeasurement[]>();
  @Output() assetMeasurementsChange = new EventEmitter<AssetMeasurement[]>();
  @Output() deviceExpressionsChange = new EventEmitter<DeviceMeasurement[]>();
  @Output() previousStep = new EventEmitter<void>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<string>();

  @Input() tenant: Tenant;
  tenantIsEmpty: boolean;

  templateMeasurements: (DeviceMeasurement | AssetMeasurement)[];
  templateMeasurementsForm: FormGroup;
  rowsForm: FormArray;

  measurementTypes: MeasurementType[];
  clonedMeasurements: { [s: string]: AbstractControl; } = {};

  displayCopyFromTemplate: boolean;
  copyFromTemplateActions: ActionButton[];

  constructor(private tenantsService: TenantsService,
              private templatesService: TemplatesService,
              private formBuilder: FormBuilder,
              private previousService: PreviousRouteService) { }

  ngOnInit(): void {
    this.templateMeasurements = (this.type === 'DEVICE') ? this.deviceMeasurements : this.assetMeasurements;
    this.buildCopyFromTemplateActions();
    if (!this.tenant) {
      this.tenantIsEmpty = true;
    } else {
      this.loadAndCreateForm();
    }
  }

  private loadAndCreateForm(): void {
    this.tenantsService.getTenantMeasurementTypes(this.tenant.uuid).subscribe(response => {
      this.measurementTypes = response.measurementTypes
        .sort((a, b) => a.sensor > b.sensor ? 1 : -1);
      this.createForm(this.templateMeasurements);
    });
  }

  private createForm(templateMeasurements: (DeviceMeasurement | AssetMeasurement)[]): void {
    if (this.type === 'DEVICE') {
      this.rowsForm = this.formBuilder.array(
        templateMeasurements.map(measurement => this.formGroupFromDeviceMeasurement(measurement as DeviceMeasurement))
      );
    } else {
      this.rowsForm = this.formBuilder.array(
        templateMeasurements.map(measurement => this.formGroupFromAssetMeasurement(measurement as AssetMeasurement))
      );
    }
    this.templateMeasurementsForm = this.formBuilder.group({
      templateMeasurements: this.rowsForm
    });
  }

  addMeasurement(): void {
    const newMeasurement = (this.type === 'DEVICE') ? this.formGroupFromDeviceMeasurement(new DeviceMeasurement())
      : this.formGroupFromAssetMeasurement(new AssetMeasurement());
    this.rowsForm.push(newMeasurement);
  }

  onRowDelete(rowIndex: number): void {
    this.rowsForm.removeAt(rowIndex);
  }

  deleteAllRows(): void {
    while (this.rowsForm.length > 0) {
      this.rowsForm.removeAt(0);
    }
  }

  onPreviousStep(): void {
    const measurements = this.templateMeasurementsForm.get('templateMeasurements').value;
    this.previousStep.emit();
    if (this.type === 'DEVICE') {
      this.deviceMeasurementsChange.emit(measurements);
    } else {
      this.assetMeasurementsChange.emit(measurements);
    }
  }

  onNextStep(): void {
    if (this.templateMeasurementsForm.valid) {
      const measurements = this.templateMeasurementsForm.get('templateMeasurements').value;
      this.nextStep.emit();
      if (this.type === 'DEVICE') {
        this.deviceMeasurementsChange.emit(measurements);
        this.deviceExpressionsChange.emit(this.deviceExpressions);
      } else {
        this.assetMeasurementsChange.emit(measurements);
      }
    }
  }

  private formGroupFromDeviceMeasurement(measurement: DeviceMeasurement): FormGroup {
    return this.formBuilder.group({
      name: [ measurement.name, [
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters
      ]],
      description: [ measurement.description, [Validators.maxLength(255)] ],
      alias: [ measurement.alias ],
      measurementType: [ measurement.measurementType, [Validators.required] ],
      sampleRate: [ measurement.sampleRate, [Validators.min(1)] ],
      expression: [ measurement.expression],
      uuid: [ measurement.uuid ]
    }, {updateOn: 'blur'});
  }

  private formGroupFromAssetMeasurement(measurement: AssetMeasurement): FormGroup {
    return this.formBuilder.group({
      name: [ measurement.name, [
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters
      ]],
      description: [ measurement.description, [Validators.maxLength(255)] ],
      alias: [ measurement.alias ],
      measurementType: [ measurement.measurementType, [Validators.required] ],
      sampleRate: [ measurement.sampleRate, [Validators.min(1)] ],
      uuid: [ measurement.uuid ]
    }, {updateOn: 'blur'});
  }

  onCancel(): void {
    this.cancel.emit(this.previousService.getPreviousUrl());
  }

  private buildCopyFromTemplateActions(): void {
    this.copyFromTemplateActions = [
      {
        label: 'misc.copySensors',
        icon: 'pi pi-copy',
        action: (template: Template) => this.copyMeasurements(template)
      }, {
        label: 'misc.appendSensors',
        icon: 'pi pi-plus',
        action: (template: Template) => this.copyMeasurements(template, true)
      }
    ];
  }

  copyMeasurements(template: Template, append: boolean = false): void {
    if (!append) {
      this.deleteAllRows();
    }
    const measurements = this.templateMeasurementsForm.get('templateMeasurements').value;
    this.templatesService.getTemplate(template.uuid).toPromise().then(response => {
      let fullTemplate = response.template;
      if (fullTemplate.type === 'DEVICE' && fullTemplate.content?.deviceMeasurements) {
        fullTemplate.content.deviceMeasurements.forEach((measurement: DeviceMeasurement) => {
          measurement.uuid = null;
          if (measurements.find(sensor => sensor.name === measurement.name) === undefined &&
            this.deviceExpressions.find(sensor => sensor.name === measurement.name) === undefined) {
            if (!measurement.expression) {
              this.rowsForm.push(this.formGroupFromDeviceMeasurement(measurement));
            } else {
              this.deviceExpressions.push(measurement);
            }
          }
        });
      } else if (fullTemplate.type === 'ASSET' && fullTemplate.content?.assetMeasurements) {
        fullTemplate.content.assetMeasurements.forEach((measurement: AssetMeasurement) => {
          measurement.uuid = null;
          if (measurements.find(sensor => sensor.name === measurement.name) === undefined) {
            this.rowsForm.push(this.formGroupFromAssetMeasurement(measurement));
          }
        });
      }
      this.displayCopyFromTemplate = false;
    });
  }

}
